import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { useNavigationData } from 'src/hooks/use-navigation-data';
import {
  getIsOrgAdmin,
  getWorkspaceOrgId,
  getCanViewEntitlement,
  getCcpEntitlementId,
  getCcpTransactionAccountId,
  getDefaultWorkspace,
} from 'src/selectors/global-selectors';
import { getRecentlyViewedWorkspacesKeys } from 'src/selectors/workspace-selectors';

// this hook utilizes our existing useNavigationData hook to pull whatever data
// we need for the new top navigation. Once this is in a more final state and we
// see what we need, we may decide to change what data our APIs return so we only
// include the info we need for the new UI. In that case we could have this
// access a different hook (maybe something like useNavSystemData()) that's
// specific to nav v4.
export const useTopNavData = () => {
  const navData = useNavigationData();

  // redux data needed (in the old nav this is mapped in using a combo of
  // selectors and the navigation state hooks)
  const defaultWorkspace = useSelector(getDefaultWorkspace);
  const isOrgAdmin = useSelector(getIsOrgAdmin);
  const workspaceOrgId = useSelector(getWorkspaceOrgId);
  const ccpEntitlementId = useSelector(getCcpEntitlementId);
  const ccpTransactionAccountId = useSelector(getCcpTransactionAccountId);
  const canViewEntitlement = useSelector(getCanViewEntitlement);
  const recentlyViewedWorkspacesKeys = useSelector(
    getRecentlyViewedWorkspacesKeys
  );

  return useMemo(
    () => ({
      // general
      isCurrentUserAnonymous: navData.isCurrentUserAnonymous,

      // account menu
      userDisplayName: navData.userDisplayName,
      userAvatarUrl: navData.userAvatarUrl,
      userEmail: navData.userEmail,
      loginUrl: navData.loginUrl,
      logoutUrl: navData.logoutUrl,
      manageAccountUrl: navData.manageAccountUrl,
      switchAccountUrl: navData.switchAccountUrl,
      userWorkspaceId: navData.userWorkspaceId,
      recentlyViewedWorkspacesKeys,

      // platform notifications
      currentWorkspace: navData.currentWorkspace,
      defaultWorkspace,

      // settings
      isOrgAdmin,
      workspaceOrgId,
      ccpEntitlementId,
      ccpTransactionAccountId,
      canViewEntitlement,
      adminHubUrl: navData.adminHubUrl,
      menuItems: navData.horizontalNavigationItems,

      // create button
      globalBitbucketActions: navData.globalBitbucketActions,
      containerBitbucketActions: navData.containerBitbucketActions,

      // app logo
      hasPride: navData.hasPride,

      // help
      whatsNewUrl: navData.whatsNewUrl,
      attributionsUrl: navData.attributionsUrl,
      supportAdminUrl: navData.supportAdminUrl,
      userUuid: navData.userUuid,
      currentUser: navData.currentUser,

      // search
      currentRepository: navData.currentRepository,

      // ... TODO: add props for other top nav items
    }),
    [
      navData.userAvatarUrl,
      navData.userDisplayName,
      navData.isCurrentUserAnonymous,
      navData.loginUrl,
      navData.userEmail,
      navData.logoutUrl,
      navData.manageAccountUrl,
      navData.switchAccountUrl,
      navData.currentWorkspace,
      navData.adminHubUrl,
      navData.horizontalNavigationItems,
      navData.globalBitbucketActions,
      navData.containerBitbucketActions,
      navData.hasPride,
      navData.whatsNewUrl,
      navData.attributionsUrl,
      navData.supportAdminUrl,
      navData.userUuid,
      navData.currentUser,
      navData.userWorkspaceId,
      navData.currentRepository,
      defaultWorkspace,
      isOrgAdmin,
      workspaceOrgId,
      ccpEntitlementId,
      ccpTransactionAccountId,
      canViewEntitlement,
      recentlyViewedWorkspacesKeys,
    ]
  );
};

export type TopNavData = ReturnType<typeof useTopNavData>;
